import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "../../../../environments/environment";
import { VehicleInterface } from "../../interface/common.interface";
import {
  MotorClonedQuotationInterface,
  MotorQuotationChangeOwnershipDetailInterface,
  MotorQuotationDetailInterface,
  MotorQuotationLinkListingInterface,
  MotorQuotationListingInterface,
  MotorQuotationNCDInfo,
  MotorQuotationPolicyDetailListingInterface,
  MotorQuotationPolicyInterface,
  MotorQuotationPolicyListingInterface,
  MotorQuotationUpdateNcd,
  MotorQuotationVehicleInfo
} from "../../interface/motor/motor-quotation.interface";
import { firstValueFrom } from "rxjs";
import { ErrorCode } from "src/app/core/enums";
import { AlertService } from "../../service/alert.service";
import { Response } from "src/app/core/models";
import { RepositionScrollStrategy } from "@angular/cdk/overlay";

@Injectable({
  providedIn: "root"
})
export class MotorQuotationDataService {
  private basePath: string = `${environment.url}/api/Motor/MotorQuotation/`;

  constructor(private http: HttpClient, private alertService: AlertService) {}

  async getYesterdayCount(): Promise<number> {
    let response: number;

    response = await firstValueFrom(
      this.http.get<number>(`${this.basePath}GetYesterdayCount`)
    );

    // [24/12/2024] - Reason for such checking is because Method not returning default errorCode & errorDesc else will be more straightforward
    if (response === null || response === undefined) {
      this.alertService.openSnackBar("Failed to retreive yesterday count");
      throw response;
    }

    return response;
  }

  async getListing(
    request: MotorQuotationRequest
  ): Promise<MotorQuotationResponse> {
    let response: MotorQuotationResponse;

    response = await firstValueFrom(
      this.http.post<MotorQuotationResponse>(
        `${this.basePath}GetListing`,
        request
      )
    );

    if (response.errorCode === ErrorCode.FAIL) {
      throw new Error();
    }

    return response;
  }

  async getMotorcycleListing(
    request: MotorcycleQuotationRequest
  ): Promise<MotorQuotationResponse> {
    let response: MotorQuotationResponse;

    response = await firstValueFrom(
      this.http.post<MotorQuotationResponse>(
        `${this.basePath}GetMotorcycleListing`,
        request
      )
    );

    if (response.errorCode === ErrorCode.FAIL) {
      throw new Error();
    }

    return response;
  }

  async getDealerMotorcycleListing(
    request: MotorcycleQuotationRequest
  ): Promise<MotorQuotationResponse> {
    let response: MotorQuotationResponse;

    response = await firstValueFrom(
      this.http.post<MotorQuotationResponse>(
        `${this.basePath}GetDealerMotorcycleListing`,
        request
      )
    );

    if (response.errorCode === ErrorCode.FAIL) {
      throw new Error();
    }

    return response;
  }

  getIQQuotationListing(request: MotorQuotationRequest) {
    return this.http.post<MotorQuotationResponse>(
      `${this.basePath}GetIQQuotationListing`,
      request
    );
  }

  async getDetailById(id: string): Promise<MotorQuotationDetailInterface> {
    let response: MotorQuotationDetailInterface;

    response = await firstValueFrom(
      this.http.get<MotorQuotationDetailInterface>(
        `${this.basePath}GetDetailById/${id}`
      )
    );

    if (response.errorCode === ErrorCode.FAIL) {
      throw new Error();
    }

    return response;
  }

  async getChangeOwnershipDetail(
    id: string
  ): Promise<MotorQuotationChangeOwnershipDetailInterface> {
    let response: MotorQuotationChangeOwnershipDetailInterface;

    response = await firstValueFrom(
      this.http.get<MotorQuotationChangeOwnershipDetailInterface>(
        `${this.basePath}GetChangeOwnershipDetail/${id}`
      )
    );

    if (response.errorCode === ErrorCode.FAIL) {
      throw new Error();
    }

    return response;
  }

  // Note:
  // Reject Type Id (from FE) is Reject Reason Id (BE)
  // Reject Reason Id (from FE) is Reject Remark (BE)
  // Reject Remark (from FE) is Reject comment (BE)
  // isInstantQuotation (from FE) is indicate it's for instant quotation (BE)
  async reject(request: RejectQuoteRequest): Promise<any> {
    let response: any;

    try {
      response = await firstValueFrom(
        this.http.post(`${this.basePath}Reject/${request.id}`, request)
      );
      this.alertService.openSnackBar("Successfully Rejected");
    } catch (error) {
      this.alertService.openSnackBar("Reject Unsuccessful");
      throw new Error();
    }

    return response;
  }

  getQuotationPolicyById(id: string) {
    return this.http.get<MotorQuotationPolicyInterface>(
      `${this.basePath}GetQuotationPolicyById/${id}`
    );
  }

  getCarInfo(isCompany: boolean, isMalaysian: boolean, vehicleNo: string, identityNo: string, postcode: string, isFoodPanda: boolean) {
    return this.http.post<{
      vehicleInfo: MotorQuotationVehicleInfo;
      seat: number;
      typeOfVehicle: string;
      ncdInfo: MotorQuotationNCDInfo;
    }>(`${this.basePath}GetVehicleInfo`, {
      isCompany,
      isMalaysian,
      vehicleNo,
      identityNo,
      postcode,
      isFoodPanda
    });
  }

  async getVehicleBodyTypeSelection(): Promise<VehicleBodyTypeData[]> {
    let response: VehicleBodyTypeData[];

    try {
      response = await firstValueFrom(
        this.http.get<VehicleBodyTypeData[]>(
          `${this.basePath}GetVehicleBodyTypeSelection`
        )
      );
    } catch (error) {
      throw new Error();
    }

    return response;
  }

  async editVehicle(id: string, val: any): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post(`${this.basePath}EditVehicle/${id}`, val)
      );

      this.alertService.openSnackBar("Successfully edited");
    } catch (error) {
      this.alertService.openSnackBar("Edit Failed");
      throw new Error();
    }
  }

  editMotorcycleVehicle(id: string, val: any) {
    return this.http.post(`${this.basePath}EditMotorcycleVehicle/${id}`, val);
  }

  updateQuotationNcd(id: string, val: MotorQuotationUpdateNcd) {
    return this.http.post(`${this.basePath}UpdateQuotationNCD/${id}`, val);
  }

  editCustomer(id: string, val: any) {
    return this.http.post(`${this.basePath}EditCustomer/${id}`, val);
  }

  editNCD(id: string, previousVehicleNo: string, ncd: number) {
    return this.http.post(`${this.basePath}UpdateNCD/${id}`, {
      previousVehicleNo,
      ncd
    });
  }

  async getVehicleInfo(
    request: GetVehicleInfoRequest
  ): Promise<VehicleInterface> {
    let response: VehicleInterface;

    response = await firstValueFrom(
      this.http.post<VehicleInterface>(
        `${this.basePath}getVehicleInfo`,
        request
      )
    );

    if (response.errorCode === ErrorCode.FAIL) {
      throw new Error();
    }

    if (!response.vehicleInfo.isFound) {
      this.alertService.openSnackBar(response.vehicleInfo.errorMsg);
    }

    if (!response.ncdInfo.isFound) {
      this.alertService.openSnackBar(response.ncdInfo.errorMsg);
    }

    return response;
  }

  async add(request: any): Promise<void> {
    try {
      await firstValueFrom(this.http.post(`${this.basePath}Add`, request));

      this.alertService.openSnackBar("Successful Added");
      window.history.back();
    } catch (error) {
      this.alertService.openSnackBar("Add Unsuccessful");
    }
  }

  approve(id: string, val: any) {
    return this.http.post(`${this.basePath}Approve/${id}`, val);
  }

  async getPolicyListing(
    request: MotorQuotationRequest
  ): Promise<MotorQuotationPolicyResponse> {
    let response: MotorQuotationPolicyResponse;

    response = await firstValueFrom(
      this.http.post<MotorQuotationPolicyResponse>(
        `${this.basePath}GetPolicyListing`,
        request
      )
    );

    if (response.errorCode === ErrorCode.FAIL) {
      throw new Error();
    }

    return response;
  }

  getMotorcyclePolicyListing(
    limit: number,
    offset: number,
    text: string,
    columnIndex: number,
    sortDirection: string,
    motorPartnerIds: string[],
    motorProductIds: string[],
    statusList: string[],
    isTransferOwnership: boolean,
    isInstantQuotation: boolean,
    startDate?: Date,
    endDate?: Date
  ) {
    return this.http.post<{
      count: any;
      data: MotorQuotationPolicyListingInterface[];
    }>(`${this.basePath}GetMotorcyclePolicyListing`, {
      limit,
      offset,
      text,
      columnIndex,
      sortDirection,
      motorPartnerIds,
      motorProductIds,
      isTransferOwnership,
      isInstantQuotation,
      statusList,
      startDate,
      endDate
    });
  }

  async getPolicyListingExcel(request: MotorQuotationRequest): Promise<any> {
    let response: any;

    try {
      response = await firstValueFrom(
        this.http.post(`${this.basePath}GetPolicyListingExcel`, request, {
          responseType: "blob"
        })
      );
    } catch (error) {
      this.alertService.openSnackBar("Failed to export data");
    }

    return response;
  }

  editRoadTax(id: string, price: number) {
    return this.http.get(`${this.basePath}EditRoadTax/${id}?price=${price}`);
  }

  getPolicyDetailListingById(id: string) {
    return this.http.get<MotorQuotationPolicyDetailListingInterface[]>(
      `${this.basePath}GetPolicyDetailListingByRequestId/${id}`
    );
  }

  getLink(id: string) {
    return this.http.get<MotorQuotationLinkListingInterface[]>(
      `${this.basePath}getLink/${id}`
    );
  }

  refreshLink(id: string) {
    return this.http.get(`${this.basePath}RefreshLink/${id}`);
  }

  async getClonedQuotationValue(
    id: string
  ): Promise<MotorClonedQuotationInterface> {
    let response: MotorClonedQuotationInterface;

    response = await firstValueFrom(
      this.http.get<MotorClonedQuotationInterface>(
        `${this.basePath}GetClonedQuotationValue/${id}`
      )
    );

    if (response?.errorCode === ErrorCode.FAIL) {
      throw new Error();
    }

    return response;
  }

  SendQuotationEmail(id: string) {
    return this.http.get(`${this.basePath}SendQuotationEmail/${id}`);
  }

  getPAndOVehicleInfo(id: string) {
    return this.http.post(`${this.basePath}GetPAndOVehicleInfo/${id}`, null);
  }

  getBulkQuotationListing(request: MotorQuotationRequest) {
    return this.http.post<{
      count: any;
      data: any[];
    }>(`${this.basePath}GetBulkQuotationListing`, request);
  }

  uploadBulkQuotationFile(request: FormData) {
    return this.http.post(`${this.basePath}UploadBulkQuotationExcel`, request, {
      responseType: "blob",
      observe: "response"
    });
  }

  downloadTemplate() {
    return this.http.get(
      `${this.basePath}DownloadPolicyBulkCancelExcelTemplate`,
      {
        responseType: "blob"
      }
    );
  }

  getInstantQuotation(id: string) {
    return this.http.get(`${this.basePath}GetD2CInstantQuotationById/${id}`);
  }

  async onHoldQuotation(id: string): Promise<any> {
    let response: any;

    try {
      response = await firstValueFrom(
        this.http.post<any>(`${this.basePath}OnHoldQuotation/${id}`, null)
      );

      this.alertService.openSnackBar("Quotation successfully on hold.");
    } catch (error) {
      this.alertService.openSnackBar("Quotation on hold unsuccessful");
      throw new Error();
    }

    return response;
  }
}

export interface MotorQuotationRequest {
  limit: number;
  offset: number;
  text: string;
  columnIndex: number;
  sortDirection: string;
  motorPartnerIds: string[];
  motorProductIds: string[];
  isTransferOwnership: boolean;
  status?: string;
  statusList?: string[];
  startDate?: Date;
  endDate?: Date;
  isInstantQuotation?: boolean;
  isMotorcycle?: boolean;
}

export interface MotorcycleQuotationRequest {
  limit: number;
  offset: number;
  text: string;
  columnIndex: number;
  sortDirection: string;
  motorPartnerIds: string[];
  motorProductIds: string[];
  isTransferOwnership: boolean;
  statusList?: string[];
  startDate?: Date;
  endDate?: Date;
  isTooEarly?: boolean;
}

interface MotorQuotationResponse extends Response {
  count: number;
  data: MotorQuotationListingInterface[];
  before6Count: number;
  after6Count: number;
}

interface MotorQuotationPolicyResponse extends Response {
  count: StatusList;
  data: MotorQuotationPolicyListingInterface[];
}

export interface GetVehicleInfoRequest {
  vehicleNo: string;
  identityNo: string;
  postcode?: string;
  isCompany?: boolean;
  isMalaysian?: boolean;
}

export interface RejectQuoteRequest {
  id: string;
  rejectReasonId: string;
  rejectRemark: string;
  comment: string;
  isInstantQuotation?: boolean;
}

interface StatusList {
  activated: number;
  available: number;
  cancelled: number;
  nonPurchased: number;
  purchased: number;
  rejected: number;
  total: number;
}

interface VehicleBodyTypeData {
  code: string;
}
